import React from "react";
import { MdEngineering, MdDashboard } from "react-icons/md";
import { FcComboChart } from "react-icons/fc";
import { IoSettingsOutline } from "react-icons/io5";
import { SiZigbee2Mqtt } from "react-icons/si";
import { AiOutlineCheckSquare } from "react-icons/ai";
import { BsClipboardData, BsTools } from "react-icons/bs";
import { MdOutlineDevices } from "react-icons/md";
import { RiBuilding2Line, RiUserSettingsLine } from "react-icons/ri";
import { BellOutlined, ApartmentOutlined } from "@ant-design/icons";

// ─────────────────────────────────────────────────────────────────────────────
// Routes
// ─────────────────────────────────────────────────────────────────────────────
const ROUTES = {
  // Dashboard
  DASHBOARD: "/",

  // Checklist Builder
  CHECKLIST:          "/check-list-builder/check-list",
  // CHECKLIST_CATEGORY removed — Category no longer exists in PDI
  CHECKLIST_TEMPLATE: "/check-list-builder/configuration/checklist-builder",
  CHECKLIST_BINDING:  "/check-list-builder/binding",

  // Quality Plan
  QUALITY_EXECUTION: "/quality/procedure",
  QUALITY_COMPORT:   "/quality/configuration",
  QUALITY_DEVICE:    "/quality/configuration/device-type",
  QUALITY_TEMPLATE:  "/quality/configuration/procedure-builder",

  // AMS
  AMS_LIVE_MONITORING: "/ams/live-monitoring",
  AMS_NOTIFICATIONS:   "/ams/notifications",

  // Configuration
  CONFIG_ORGANIZATION: "/configuration",
  CONFIG_PRODUCT:      "/configuration/product",
  CONFIG_IOT_GATEWAY:  "/configuration/iot-gateway",
  CONFIG_MACHINE:      "/configuration/machine",
  CONFIG_ROLE:         "/configuration/role",
  CONFIG_USER:         "/configuration/user",
  CONFIG_USER_ACCESS:  "/configuration/user-access",
};

// ─────────────────────────────────────────────────────────────────────────────
// MODULE_CONFIG — used by dashboard cards (getModulesFromMenu)
// ─────────────────────────────────────────────────────────────────────────────
const MODULE_CONFIG = [
  {
    key: "CHECKLIST_BUILDER",
    label: "Checklist Builder",
    description: "Checklist execution, binding & templates",
    icon: <AiOutlineCheckSquare />,
    accent: "#3b82f6",
    bg: "#E6F1FB",
    screens: [
      { key: ROUTES.CHECKLIST,         label: "Checklist", permKey: "CHECKLIST",         icon: <AiOutlineCheckSquare /> },
      { key: ROUTES.CHECKLIST_TEMPLATE, label: "Templates", permKey: "CHECKLIST_TEMPLATE", icon: <BsClipboardData /> },
      { key: ROUTES.CHECKLIST_BINDING,  label: "Binding",   permKey: "CHECKLIST_BINDING",  icon: <ApartmentOutlined /> },
    ],
  },
  {
    key: "QUALITY_PLAN",
    label: "Quality Plan",
    description: "Quality procedure execution & configuration",
    icon: <BsTools />,
    accent: "#10b981",
    bg: "#E1F5EE",
    screens: [
      { key: ROUTES.QUALITY_EXECUTION, label: "Execution",        permKey: "QUALITY_EXECUTION", icon: <AiOutlineCheckSquare /> },
      { key: ROUTES.QUALITY_COMPORT,   label: "Comport Settings", permKey: "QUALITY_COMPORT",   icon: <SiZigbee2Mqtt /> },
      { key: ROUTES.QUALITY_DEVICE,    label: "Device Settings",  permKey: "QUALITY_DEVICE",    icon: <MdOutlineDevices /> },
      { key: ROUTES.QUALITY_TEMPLATE,  label: "Templates",        permKey: "QUALITY_TEMPLATE",  icon: <BsClipboardData /> },
    ],
  },
  {
    key: "AMS",
    label: "Asset Monitoring System",
    description: "Live monitoring & write control",
    icon: <FcComboChart />,
    accent: "#f59e0b",
    bg: "#FAF0E6",
    screens: [
      { key: ROUTES.AMS_LIVE_MONITORING, label: "Live Monitoring", permKey: "AMS_LIVE_MONITORING", icon: <FcComboChart /> },
      { key: ROUTES.AMS_NOTIFICATIONS,   label: "Notifications",   permKey: "AMS_NOTIFICATIONS",   icon: <BellOutlined /> },
    ],
  },
  {
    key: "CONFIGURATION",
    label: "Configuration",
    description: "System settings & administration",
    icon: <IoSettingsOutline />,
    accent: "#8b5cf6",
    bg: "#F1EFE8",
    screens: [
      { key: ROUTES.CONFIG_ORGANIZATION, label: "Organization", permKey: "CONFIG_ORGANIZATION", icon: <RiBuilding2Line /> },
      { key: ROUTES.CONFIG_PRODUCT,      label: "Product",      permKey: "CONFIG_PRODUCT",      icon: <BsTools /> },
      { key: ROUTES.CONFIG_IOT_GATEWAY,  label: "IoT Gateway",  permKey: "CONFIG_IOT_GATEWAY",  icon: <SiZigbee2Mqtt /> },
      { key: ROUTES.CONFIG_MACHINE,      label: "Machine",      permKey: "CONFIG_MACHINE",      icon: <MdEngineering /> },
      { key: ROUTES.CONFIG_ROLE,         label: "Role",         permKey: "CONFIG_ROLE",         icon: <IoSettingsOutline /> },
      { key: ROUTES.CONFIG_USER,         label: "User",         permKey: "CONFIG_USER",         icon: <RiUserSettingsLine /> },
      { key: ROUTES.CONFIG_USER_ACCESS,  label: "User Access",  permKey: "CONFIG_USER_ACCESS",  icon: <RiUserSettingsLine /> },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// buildMenuItems — Ant Design sidebar Menu items
// ─────────────────────────────────────────────────────────────────────────────
export const buildMenuItems = ({ can, userDetails } = {}) => {
  const isSuperAdmin =
    userDetails?.role  === "ROLE_SUPER_ADMIN" ||
    userDetails?.roles === "ROLE_SUPER_ADMIN";

  const allowed = (permKey) => isSuperAdmin || (can && can(permKey, "read"));

  // ── 1. Checklist Builder ──────────────────────────────────────────────────
  // Category removed. Configuration sub-menu now only contains Templates.
  // Binding is a top-level sibling under Checklist Builder.
  const checklistConfigChildren = [
    allowed("CHECKLIST_TEMPLATE") && { key: ROUTES.CHECKLIST_TEMPLATE, label: "Templates", icon: <BsClipboardData /> },
  ].filter(Boolean);

  const checklistChildren = [
    allowed("CHECKLIST")         && { key: ROUTES.CHECKLIST,        label: "Execution", icon: <AiOutlineCheckSquare /> },
    allowed("CHECKLIST_BINDING") && { key: ROUTES.CHECKLIST_BINDING, label: "Binding",   icon: <ApartmentOutlined /> },
    checklistConfigChildren.length > 0 && {
      key: "CHECKLIST_CONFIG",
      label: "Configuration",
      icon: <IoSettingsOutline />,
      children: checklistConfigChildren,
    },
  ].filter(Boolean);

  // ── 2. Quality Plan ───────────────────────────────────────────────────────
  const qualityConfigChildren = [
    allowed("QUALITY_COMPORT")  && { key: ROUTES.QUALITY_COMPORT,  label: "Comport Settings", icon: <SiZigbee2Mqtt /> },
    allowed("QUALITY_DEVICE")   && { key: ROUTES.QUALITY_DEVICE,   label: "Device Settings",  icon: <MdOutlineDevices /> },
    allowed("QUALITY_TEMPLATE") && { key: ROUTES.QUALITY_TEMPLATE, label: "Templates",        icon: <BsClipboardData /> },
  ].filter(Boolean);

  const qualityChildren = [
    allowed("QUALITY_EXECUTION") && { key: ROUTES.QUALITY_EXECUTION, label: "Execution", icon: <AiOutlineCheckSquare /> },
    qualityConfigChildren.length > 0 && {
      key: "QUALITY_CONFIG",
      label: "Configuration",
      icon: <IoSettingsOutline />,
      children: qualityConfigChildren,
    },
  ].filter(Boolean);

  // ── 3. AMS ────────────────────────────────────────────────────────────────
  const amsChildren = [
    allowed("AMS_LIVE_MONITORING") && { key: ROUTES.AMS_LIVE_MONITORING, label: "Live Monitoring", icon: <FcComboChart /> },
    allowed("AMS_NOTIFICATIONS")   && { key: ROUTES.AMS_NOTIFICATIONS,   label: "Notifications",   icon: <BellOutlined /> },
  ].filter(Boolean);

  // ── 4. Configuration ─────────────────────────────────────────────────────
  const configChildren = [
    allowed("CONFIG_ORGANIZATION") && { key: ROUTES.CONFIG_ORGANIZATION, label: "Organization", icon: <RiBuilding2Line /> },
    allowed("CONFIG_PRODUCT")      && { key: ROUTES.CONFIG_PRODUCT,      label: "Product",      icon: <BsTools /> },
    allowed("CONFIG_IOT_GATEWAY")  && { key: ROUTES.CONFIG_IOT_GATEWAY,  label: "IoT Gateway",  icon: <SiZigbee2Mqtt /> },
    allowed("CONFIG_MACHINE")      && { key: ROUTES.CONFIG_MACHINE,      label: "Machine",      icon: <MdEngineering /> },
    allowed("CONFIG_ROLE")         && { key: ROUTES.CONFIG_ROLE,         label: "Role",         icon: <IoSettingsOutline /> },
    allowed("CONFIG_USER")         && { key: ROUTES.CONFIG_USER,         label: "User",         icon: <RiUserSettingsLine /> },
    allowed("CONFIG_USER_ACCESS")  && { key: ROUTES.CONFIG_USER_ACCESS,  label: "User Access",  icon: <RiUserSettingsLine /> },
  ].filter(Boolean);

  return [
    { key: ROUTES.DASHBOARD, label: "Dashboard", icon: <MdDashboard /> },
    checklistChildren.length > 0 && {
      key: "CHECKLIST_BUILDER",
      label: "Checklist Builder",
      icon: <AiOutlineCheckSquare />,
      children: checklistChildren,
    },
    qualityChildren.length > 0 && {
      key: "QUALITY_PLAN",
      label: "Quality Plan",
      icon: <BsTools />,
      children: qualityChildren,
    },
    amsChildren.length > 0 && {
      key: "AMS",
      label: "Asset Monitoring System",
      icon: <FcComboChart />,
      children: amsChildren,
    },
    configChildren.length > 0 && {
      key: "CONFIGURATION",
      label: "Configuration",
      icon: <IoSettingsOutline />,
      children: configChildren,
    },
  ].filter(Boolean);
};

// ─────────────────────────────────────────────────────────────────────────────
// getModulesFromMenu — dashboard cards
// ─────────────────────────────────────────────────────────────────────────────
export const getModulesFromMenu = ({ can, userDetails } = {}) => MODULE_CONFIG;

export default MODULE_CONFIG;